import axios, { AxiosInstance, AxiosError } from 'axios';
import { toast } from 'sonner';

export const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

const TOKEN_KEY = 'beat_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// ---- "View as": an admin holding a read-only token for another person ----------
// The admin's own token is parked so Exit restores it without a new sign-in.
const ADMIN_TOKEN_KEY = 'beat_admin_token';
const VIEW_AS_KEY = 'beat_view_as';
export interface ViewAs { id: string; display_name: string; role: string; started_at: number; expires_at: number }
export function getViewAs(): ViewAs | null {
  try { const v = localStorage.getItem(VIEW_AS_KEY); return v ? (JSON.parse(v) as ViewAs) : null; } catch { return null; }
}
/** The admin's own token while a view-as is on; used to ask for another view-as token. */
export function getAdminToken(): string | null {
  try { return localStorage.getItem(ADMIN_TOKEN_KEY); } catch { return null; }
}
export function startViewAs(token: string, info: ViewAs) {
  // Started from inside a view-as (another clinic of the same doctor): the parked
  // admin token stays; the current token is the old view-as one, not the admin's.
  const mine = getToken();
  if (mine && !getAdminToken()) localStorage.setItem(ADMIN_TOKEN_KEY, mine);
  localStorage.setItem(VIEW_AS_KEY, JSON.stringify(info));
  setToken(token);
}
export function endViewAs() {
  const mine = localStorage.getItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(VIEW_AS_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  if (mine) setToken(mine); else clearToken();
}

// ---- Keeping the session alive (Farouk 2026-09-29) ------------------------------
// A token lasts 24 h and nothing renewed it, so a long upload day ended on the login
// page. While the app is open it swaps the token for a fresh one once less than 20 h
// is left: in practice every few hours, and never more than once per check.
const RENEW_BELOW_MS = 20 * 60 * 60 * 1000;
function tokenExpiresAt(t: string): number | null {
  try {
    const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof p.exp === 'number' ? p.exp * 1000 : null;
  } catch { return null; }
}
let renewing = false;
export async function renewSession(): Promise<void> {
  const t = getToken();
  if (!t || renewing || getViewAs()) return;
  const exp = tokenExpiresAt(t);
  if (exp === null || exp <= Date.now() || exp - Date.now() > RENEW_BELOW_MS) return;
  renewing = true;
  try {
    const r = await axios.post<{ access_token: string }>(`${API_BASE}/auth/refresh`, null,
      { headers: { Authorization: `Bearer ${t}` }, timeout: 20_000 });
    // only if nobody signed in as someone else meanwhile
    if (getToken() === t && r.data?.access_token) setToken(r.data.access_token);
  } catch { /* the next check tries again; a dead token meets the 401 path as before */ }
  finally { renewing = false; }
}
let keeperStarted = false;
export function startSessionKeeper() {
  if (keeperStarted) return;
  keeperStarted = true;
  void renewSession();
  window.setInterval(() => { void renewSession(); }, 10 * 60 * 1000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void renewSession(); });
  window.addEventListener('focus', () => { void renewSession(); });
}

// The upload center tells us when files are moving: a sign-in that runs out then must
// not navigate away, because leaving the page kills every transfer in flight.
let uploadsBusy: () => boolean = () => false;
export function setUploadsBusyProbe(fn: () => boolean) { uploadsBusy = fn; }
let signInAsked = false;
function askToSignInKeepingUploads() {
  window.dispatchEvent(new Event('kaf:signed-out'));
  if (signInAsked) return;
  signInAsked = true;
  toast.error('Your sign-in ran out. Sign in again in a new tab: the uploads here wait and carry on by themselves.', {
    duration: Infinity,
    action: { label: 'Sign in', onClick: () => { signInAsked = false; window.open('/login', '_blank'); } },
    onDismiss: () => { signInAsked = false; },
  });
}
// A sign-in in another tab lands here: running uploads pick the new token up.
window.addEventListener('storage', (e) => {
  if (e.key === TOKEN_KEY && e.newValue) { signInAsked = false; toast.dismiss(); window.dispatchEvent(new Event('kaf:signed-in')); }
});

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 20_000,
});

api.interceptors.request.use((cfg) => {
  const token = getToken();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError<{ detail?: unknown }>) => {
    // FastAPI answers a 422 with a LIST of {loc, msg, type}. Handed to a toast as-is that
    // is a React child of the wrong kind and the whole page falls over (error #31).
    const d = err.response?.data?.detail;
    if (Array.isArray(d)) {
      const parts = d.map((x) => (x && typeof x === 'object' && 'msg' in x)
        ? `${Array.isArray((x as { loc?: unknown[] }).loc) ? ((x as { loc: unknown[] }).loc.filter((p) => p !== 'body').join('.') + ': ') : ''}${String((x as { msg: unknown }).msg)}`
        : String(x));
      (err.response!.data as { detail?: unknown }).detail = parts.join('; ') || 'The request was not valid';
    } else if (d && typeof d === 'object') {
      (err.response!.data as { detail?: unknown }).detail = JSON.stringify(d);
    }
    if (err.response?.status === 401) {
      // A view-as token expiring (30 min) must hand the admin back their own
      // session, not throw them out.
      if (getViewAs()) {
        endViewAs();
        window.location.href = '/admin';
        return Promise.reject(err);
      }
      clearToken();
      if (uploadsBusy()) { askToSignInKeepingUploads(); return Promise.reject(err); }
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    } else {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail ? detail : err.message);
    }
    return Promise.reject(err);
  }
);
